import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import GradientIcon from 'components/ui/GradientIcon';
import ContactMethodModal from 'components/modals/ContactMethodModal';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(-1);
  const [showContactModal, setShowContactModal] = useState(false);
  const [isFAQSectionExpanded, setIsFAQSectionExpanded] = useState(false);

  const handleContactMethod = (method) => {
    if (method === 'whatsapp') {
      const phoneNumber = '254793863320';
      const message = encodeURIComponent('Hi! I have questions about your academic assistance service. Can you help me?');
      window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    } else if (method === 'discord') {
      const discordInvite = 'https://discord.gg/your-server';
      window.open(discordInvite, '_blank');
    }
  };

  const faqs = [
    {
      question: "How does your transparent pricing work?",
      answer: `Our pricing is completely upfront and honest. You pay $8 per page for standard assignments with 3+ days deadline, with clear pricing tiers for urgent deadlines. There are no hidden fees, no consultation charges, and no surprise costs. What you see is exactly what you pay - unlike competitors who require lengthy consultations before quoting prices.

The $199 full class management option covers all assignments for one entire course throughout the semester, which typically saves students 40% compared to individual assignment pricing.`,
      category: "Pricing",
      icon: "DollarSign"
    },
    {
      question: "Is your service plagiarism-free and academically ethical?",
      answer: `Absolutely. Every assignment is written from scratch by our expert writers and comes with a comprehensive plagiarism report. We use advanced plagiarism detection software to ensure 100% originality.

Our work is designed to serve as reference material and study guides to help you understand concepts better. We follow strict academic integrity guidelines and provide properly cited, well-researched content that complies with academic standards. All sources are properly attributed and formatted according to your required citation style.`,
      category: "Quality",
      icon: "Shield"
    },
    {
      question: "How quickly can you complete my assignment?",
      answer: `We offer flexible deadlines to match your needs:
• 3+ Days: $8/page (standard delivery)
• 24-48 Hours: $12/page (priority handling)
• 12-24 Hours: $16/page (rush delivery)
• Under 12 Hours: $24/page (emergency service)

Our average delivery time is actually faster than quoted, with 99.8% on-time delivery rate. For urgent assignments, we have dedicated experts available 24/7 who can start working immediately.`,
      category: "Delivery",
      icon: "Clock"
    },
    {
      question: "What subjects and academic levels do you cover?",
      answer: `We cover 100+ subjects across all academic disciplines including:
• STEM & Engineering (Math, Computer Science, Physics, Chemistry)
• Business & Economics (MBA, Finance, Marketing, Management)
• Humanities & Social Sciences (English, History, Psychology, Philosophy)
• Health & Life Sciences (Nursing, Biology, Medicine, Public Health)

Our 500+ expert writers hold advanced degrees (Master's and PhD) and are matched to your specific subject and academic level - from high school to doctoral dissertations.`,
      category: "Coverage",
      icon: "BookOpen"
    },
    {
      question: "How do I communicate with my assigned expert?",
      answer: `We offer multiple communication channels for your convenience:

WhatsApp: Instant messaging with 2-minute average response time, file sharing, and real-time updates throughout your assignment progress.

Discord: Join our student community for peer support, expert Q&A sessions, and academic discussions.

Email: For formal communication, detailed explanations, and document sharing.

You'll be connected directly with your assigned expert and can communicate throughout the entire process to ensure your requirements are perfectly met.`,
      category: "Communication",
      icon: "MessageCircle"
    },
    {
      question: "What if I\'m not satisfied with the completed work?",
      answer: `Your satisfaction is guaranteed with our comprehensive protection:

• 3 free revisions until the work meets all your requirements
• Direct communication with your expert for clarifications and adjustments
• 100% money-back guarantee if you're not completely satisfied
• No questions asked refund policy

We have a 98.5% customer satisfaction rate, and less than 2% of orders require refunds due to our rigorous quality standards and revision process.`,
      category: "Guarantee",
      icon: "RefreshCw"
    },
    {
      question: "Is my personal information and assignment details secure?",
      answer: `Your privacy and security are our top priorities:

• Anonymous order processing - no need to share your real name
• 256-bit SSL encryption protects all data transmission
• GDPR compliant data handling and storage
• No third-party sharing of your information
• Secure payment processing through PayPal and Stripe
• Option to delete all data after assignment completion

We understand the importance of confidentiality in academic assistance and maintain the highest security standards to protect your identity and academic privacy.`,
      category: "Privacy",
      icon: "Lock"
    },
    {
      question: "How do I get started and place an order?",
      answer: `Getting started is incredibly simple:

1. Contact us via WhatsApp or fill out our order form
2. Share your assignment details, deadline, and requirements
3. Receive instant price calculation (no consultation needed)
4. Make secure payment through PayPal or Stripe
5. Get matched with your expert within 5 minutes
6. Receive regular updates and your completed assignment on time

The entire process takes less than 10 minutes to get started, and you'll be connected with your expert immediately to begin work on your assignment.`,
      category: "Process",
      icon: "Play"
    }
  ];

  const categories = [...new Set(faqs.map(faq => faq.category))];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? -1 : index);
  };

  return (
    <section id="faq" className="py-20 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            <span className="text-gradient">Frequently Asked</span> Questions
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Get instant answers to the most common questions about our academic assistance service. 
            Can't find what you're looking for? Contact us directly for personalized help.
          </p>
        </motion.div>

        {/* FAQ Section Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <button
            onClick={() => setIsFAQSectionExpanded(!isFAQSectionExpanded)}
            className={`backdrop-blur-xl bg-gradient-to-br from-white/15 to-white/10 border border-white/30 rounded-xl px-8 py-4 transition-all duration-300 shadow-lg inline-flex items-center space-x-3 ${
              isFAQSectionExpanded 
                ? 'from-purple-500/25 to-purple-500/15 border-purple-400/40 shadow-xl shadow-purple-500/25' 
                : 'hover:from-white/20 hover:to-white/15 hover:border-white/40 hover:shadow-white/10'
            }`}
          >
            <GradientIcon
              name="HelpCircle"
              size={24}
              gradient="purple"
              containerClassName="w-8 h-8 rounded-lg"
            />
            <span className="text-white font-bold text-lg drop-shadow-sm">
              {isFAQSectionExpanded ? 'Hide FAQs' : 'View Frequently Asked Questions'}
            </span>
            <motion.div
              animate={{ rotate: isFAQSectionExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <Icon name="ChevronDown" size={24} className="text-white/80" />
            </motion.div>
          </button>
        </motion.div>

        {/* Collapsible FAQ Content */}
        <AnimatePresence>
          {isFAQSectionExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="overflow-hidden"
            >
              {/* FAQ Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {categories.map((category, index) => (
            <motion.span
              key={category}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm border border-purple-400/30 text-purple-400 px-4 py-2 rounded-full text-sm font-medium shadow-lg hover:shadow-purple-400/25 hover:bg-purple-400/20 transition-all duration-300"
            >
              {category}
            </motion.span>
          ))}
        </motion.div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`backdrop-blur-lg bg-white/10 border border-white/20 rounded-xl overflow-hidden transition-all duration-300 ${
                openFAQ === index ? 'bg-purple-500/20 border-purple-400/30 shadow-lg shadow-purple-500/20' : 'hover:bg-white/15'
              }`}
            >
              {/* Question */}
              <button
                onClick={() => toggleFAQ(index)}
                className={`w-full p-6 text-left flex items-center justify-between transition-all duration-300 ${
                  openFAQ === index 
                    ? 'bg-purple-500/10 hover:bg-purple-500/15' 
                    : 'hover:bg-white/10'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <GradientIcon
                    name={faq.icon}
                    size={20}
                    gradient="purple"
                    showBackground={true}
                    containerClassName="w-10 h-10 rounded-lg flex-shrink-0"
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">
                      {faq.question}
                    </h3>
                    <div className="text-sm text-text-muted mt-1">
                      {faq.category}
                    </div>
                  </div>
                </div>
                <motion.div
                  animate={{ rotate: openFAQ === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Icon name="ChevronDown" size={24} className="text-text-secondary" />
                </motion.div>
              </button>

              {/* Answer */}
              <motion.div
                initial={false}
                animate={{
                  height: openFAQ === index ? 'auto' : 0,
                  opacity: openFAQ === index ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-6">
                  <div className="pl-14">
                    <div className="prose prose-sm max-w-none text-text-secondary">
                      {faq.answer.split('\n\n').map((paragraph, pIndex) => (
                        <p key={pIndex} className="mb-4 last:mb-0">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Still Have Questions CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16 bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-2xl border border-primary/20"
        >
          <GradientIcon name="HelpCircle" size={48} gradient="primary" containerClassName="w-16 h-16 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-text-primary mb-4">
            Still Have Questions?
          </h3>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto mb-6">
            Our support team is available 24/7 to answer any questions you might have. 
            Get personalized assistance and start your assignment today.
          </p>
          <button
            onClick={() => setShowContactModal(true)}
            className="btn-primary text-lg px-8 py-4 inline-flex items-center space-x-2"
          >
            <GradientIcon name="MessageCircle" size={24} gradient="secondary" />
            <span>Talk to us directly</span>
          </button>

          {/* Contact Method Modal */}
          <ContactMethodModal 
            isOpen={showContactModal} 
            onClose={() => setShowContactModal(false)} 
            onSelectMethod={handleContactMethod}
            title="How would you like to get help?"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;